import { Modal } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import OccurrenceReviewModal from '../../pages/RefactorOccurrences/presentation/views/create_edit/components/review_modal';
import {
  getOccurrenceChangesByOccurrenceId,
  IOccurrenceChange,
  IOccurrenceChangesMeta
} from '../../services/functions/occurrences';
import {
  ActionButton,
  ChangeTypeTag,
  DiffPathList,
  DiffPathTag,
  DiffText,
  EmptyState,
  ErrorState,
  Footer,
  Header,
  ModalContent,
  PaginationActions,
  PaginationInfo,
  PrimaryButton,
  Subtitle,
  Table,
  TableWrapper,
  Title
} from './index.styled';

type Props = {
  open: boolean;
  occurrenceId?: string;
  token: string;
  onClose: () => void;
};

const DEFAULT_META: IOccurrenceChangesMeta = {
  page: 1,
  take: 10,
  itemCount: 0,
  pageCount: 0,
  hasPreviousPage: false,
  hasNextPage: false
};

const formatDateTime = (value: string) => {
  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString('pt-BR');
};

const formatActor = (change: IOccurrenceChange) => {
  console.log('CHANGE: ', change);
  const userName = change.user?.name || change.user?.nickname;

  if (userName) {
    return userName;
  }

  if (!change.userId) return 'Sistema';

  return 'Desconhecido';
};

const getDiffPreview = (change: IOccurrenceChange) => {
  if (!change.diff?.length) {
    return [] as string[];
  }

  return change.diff.slice(0, 3).map(item => item.path);
};

const getChangeTypeLabel = (changeType: string) => {
  const labels: Record<string, string> = {
    create: 'Criação',
    update: 'Atualização',
    delete: 'Exclusão',
    approve: 'Aprovação',
    disapprove: 'Reprovação',
    unapprove: 'Desaprovação'
  };

  return labels[changeType] || changeType;
};

const getChangeTypeTone = (
  changeType: string
): 'neutral' | 'success' | 'danger' | 'warning' => {
  if (changeType === 'create' || changeType === 'approve') return 'success';
  if (changeType === 'delete' || changeType === 'disapprove') return 'danger';
  if (changeType === 'update' || changeType === 'unapprove') return 'warning';

  return 'neutral';
};

const OccurrenceChangesModal: React.FC<Props> = ({
  open,
  occurrenceId,
  token,
  onClose
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [changes, setChanges] = useState<IOccurrenceChange[]>([]);
  const [meta, setMeta] = useState<IOccurrenceChangesMeta>(DEFAULT_META);
  const [page, setPage] = useState(1);
  const [selectedChange, setSelectedChange] =
    useState<IOccurrenceChange | null>(null);

  const refresh = useCallback(async () => {
    if (!occurrenceId || !token) return;

    setIsLoading(true);
    setErrorMessage('');

    const response = await getOccurrenceChangesByOccurrenceId(
      token,
      occurrenceId,
      page,
      10,
      'DESC'
    );

    if (!response) {
      setChanges([]);
      setMeta(DEFAULT_META);
      setErrorMessage('Não foi possível carregar o histórico da ocorrência.');
      setIsLoading(false);
      return;
    }

    setChanges(response.data || []);
    setMeta(response.meta || DEFAULT_META);
    setIsLoading(false);
  }, [occurrenceId, page, token]);

  useEffect(() => {
    if (!open) return;
    refresh();
  }, [open, refresh]);

  useEffect(() => {
    if (!open) {
      setPage(1);
      setChanges([]);
      setMeta(DEFAULT_META);
      setSelectedChange(null);
      setErrorMessage('');
    }
  }, [open]);

  const totalLabel = useMemo(() => {
    if (!meta.itemCount) return 'Nenhuma alteração registrada';

    return `${meta.itemCount} ${
      meta.itemCount > 1 ? 'alterações' : 'alteração'
    } encontrada${meta.itemCount > 1 ? 's' : ''}`;
  }, [meta.itemCount]);

  return (
    <>
      <Modal
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
        open={open}
        onClose={onClose}
      >
        <ModalContent>
          <Header>
            <Title>Histórico de alterações</Title>
            <Subtitle>
              {occurrenceId
                ? `Ocorrência ${occurrenceId}`
                : 'Ocorrência não informada'}
            </Subtitle>
          </Header>

          {!occurrenceId ? (
            <EmptyState>
              Selecione uma ocorrência para visualizar o histórico.
            </EmptyState>
          ) : null}

          {isLoading ? <EmptyState>Carregando alterações...</EmptyState> : null}

          {!isLoading && !!errorMessage ? (
            <ErrorState>{errorMessage}</ErrorState>
          ) : null}

          {!isLoading && !errorMessage && occurrenceId ? (
            <>
              {!changes.length ? (
                <EmptyState>
                  Nenhuma alteração registrada para esta ocorrência.
                </EmptyState>
              ) : (
                <TableWrapper>
                  <Table>
                    <thead>
                      <tr>
                        <th>Diff</th>
                        <th>Tipo</th>
                        <th>Quem</th>
                        <th>Quando</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {changes.map(change => (
                        <tr key={change.id}>
                          <td>
                            {change.diff?.length ? (
                              <>
                                <DiffPathList>
                                  {getDiffPreview(change).map(path => (
                                    <DiffPathTag key={`${change.id}-${path}`}>
                                      {path}
                                    </DiffPathTag>
                                  ))}
                                </DiffPathList>
                                {change.diff.length > 3 ? (
                                  <DiffText>
                                    +{change.diff.length - 3} alterações
                                  </DiffText>
                                ) : null}
                              </>
                            ) : (
                              <DiffText>Sem alterações de campos.</DiffText>
                            )}
                          </td>
                          <td>
                            <ChangeTypeTag
                              $tone={getChangeTypeTone(change.change_type)}
                            >
                              {getChangeTypeLabel(change.change_type)}
                            </ChangeTypeTag>
                          </td>
                          <td>{formatActor(change)}</td>
                          <td>{formatDateTime(change.createdAt)}</td>
                          <td>
                            <PrimaryButton
                              onClick={() => setSelectedChange(change)}
                            >
                              Ver detalhes
                            </PrimaryButton>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableWrapper>
              )}

              <Footer>
                <PaginationInfo>
                  {totalLabel} - Página {meta.page || 1} de{' '}
                  {meta.pageCount || 1}
                </PaginationInfo>
                <PaginationActions>
                  <ActionButton
                    disabled={!meta.hasPreviousPage}
                    onClick={() =>
                      setPage(previousPage => Math.max(1, previousPage - 1))
                    }
                  >
                    Anterior
                  </ActionButton>
                  <ActionButton
                    disabled={!meta.hasNextPage}
                    onClick={() => setPage(previousPage => previousPage + 1)}
                  >
                    Próxima
                  </ActionButton>
                  <ActionButton onClick={onClose}>Fechar</ActionButton>
                </PaginationActions>
              </Footer>
            </>
          ) : null}
        </ModalContent>
      </Modal>

      <OccurrenceReviewModal
        open={!!selectedChange}
        onClose={() => setSelectedChange(null)}
        mode="audit"
        auditSnapshot={
          selectedChange?.snapshot_after ||
          selectedChange?.snapshot_before ||
          null
        }
        auditDiff={selectedChange?.diff || []}
        auditChangeType={selectedChange?.change_type}
        auditCreatedAt={selectedChange?.createdAt}
        auditAuthor={selectedChange ? formatActor(selectedChange) : undefined}
      />
    </>
  );
};

export default OccurrenceChangesModal;
